import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { CategoryEdit } from './CategoryEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { OkButton } from '@components/Form/OkButton';
import { FilterRecord, TableActions } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import { CategoryService } from '@services/Category';

type CategoryContextType = {
  captureRefreshFunction: (refreshFn: () => void) => void;
};

export const CategoryList: React.FC = () => {
  const { captureRefreshFunction } = useOutletContext<CategoryContextType>();
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [categoryName, setCategoryName] = useState<string>('');
  const [queryFilter, setQueryFilter] = useState<string>('');
  const { showError } = useNotification();

  // Create a service wrapper that matches useSyncfusionGrid interface
  const categoryServiceWrapper = {
    ...CategoryService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return CategoryService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  // Custom hooks for grid functionality
  const { filters, updateFilter, clearFilters, applyFilters } = useGridFilters(
    { nameFilter: '' },
    setQueryFilter
  );

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
    editOptions,
    pageSettings,
  } = useSyncfusionGrid({
    service: categoryServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  const { handleDelete: handleDeleteOperation } = useGridOperations({
    service: CategoryService,
    refreshGrid: fetchData,
    entityName: 'category',
  });

  useEffect(() => {
    captureRefreshFunction(fetchData);
  }, [captureRefreshFunction, fetchData]);

  const handleEdit = (rowId: any) => {
    setSelectedCategoryId(rowId);
    setShowEditCategory(true);
  };

  const handleDelete = async (rowId: number, rowCategoryName: string) => {
    try {
      setSelectedCategoryId(rowId);
      setCategoryName(rowCategoryName);
      setShowDelete(true);
    } catch (error) {
      const errorMessage = `Failed to handle delete action for category ${rowCategoryName}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const confirmDelete = () => {
    handleDeleteOperation(selectedCategoryId, categoryName);
    setShowDelete(false);
  };

  const actionButtons = (props: any) => {
    return (
      <TableActions
        dropdownActions={[
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
          },
          {
            showTopSeparator: true,
            title: 'Delete',
            onClick: () => handleDelete(props.id, props.name),
          },
        ]}
      />
    );
  };

  return (
    <>
      <FilterRecord
        label="Filter"
        value={filters.nameFilter || ''}
        onChange={(e) => updateFilter('nameFilter', e.target.value)}
        onFilter={() => {
          applyFilters(() => fetchData(true));
        }}
        onClear={() => {
          clearFilters();
          applyFilters(() => fetchData(true));
        }}
      />

      <Space />

      <GridComponent
        key={gridKey}
        dataBound={renderComplete}
        dataSource={gridData}
        ref={gridRef}
        allowPaging={true}
        pageSettings={pageSettings}
        editSettings={editOptions}
        dataStateChange={dataStateChange}
      >
        <ColumnsDirective>
          <ColumnDirective field="name" headerText="Name" />
          <ColumnDirective field="description" headerText="Description" />
          <ColumnDirective
            headerText="Actions"
            template={actionButtons}
            width={100}
          />
        </ColumnsDirective>
        <Inject services={[Page, Toolbar]} />
      </GridComponent>

      {showEditCategory && (
        <>
          <Modal show={true}>
            {selectedCategoryId && (
              <CategoryEdit
                onClose={(refreshGrid?: boolean) => {
                  setShowEditCategory(false);
                  if (refreshGrid) {
                    fetchData(true);
                  }
                }}
                onDelete={() => {
                  setShowEditCategory(false);
                  handleDelete(selectedCategoryId, categoryName);
                }}
                categoryId={selectedCategoryId}
              />
            )}
          </Modal>
        </>
      )}

      {showDelete && (
        <>
          <Modal show={true}>
            <CloseButton onClick={() => setShowDelete(false)} />
            <Box size="medium">
              <>
                <div className="px-4 pb-2">
                  <span>Are you sure to delete Category: {categoryName}</span>
                </div>
                <FormButtonsContainer>
                  <div className="space-x-2">
                    <CancelButton onClick={() => setShowDelete(false)} />
                    <OkButton onClick={confirmDelete}>Delete</OkButton>
                  </div>
                </FormButtonsContainer>
              </>
            </Box>
          </Modal>
        </>
      )}
    </>
  );
};
